"use client"

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import type { ExternalMCPServer } from "@/schema"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin"
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  PASTE_COMMAND,
  type TextNode,
} from "lexical"
import { CircleAlert, Loader2, Plug } from "lucide-react"
import { createPortal } from "react-dom"

import { usePromptInputAttachments } from "@/components/ai-elements/prompt-input"
import { Editor, type EditorRefHandle } from "@/components/lexical/editors/core"
import { $createExternalMCPMentionNode } from "@/components/lexical/nodes/external-mcp-mention/node"

export interface ExternalMCPPromptEditorHandle {
  clear: () => void
  focus: () => void
  getValue: () => string
  setValue: (value: string) => void
}

interface ExternalMCPPromptEditorProps {
  servers: ExternalMCPServer[]
  placeholder: string
  submitDisabled: boolean
  canReconnect: boolean
  reconnecting: boolean
  onReconnect: (server: ExternalMCPServer) => void
}

class ExternalMCPOption extends MenuOption {
  server: ExternalMCPServer

  constructor(server: ExternalMCPServer) {
    super(server.id)
    this.server = server
  }
}

function setEditorText(editor: EditorRefHandle | null, value: string, servers: ExternalMCPServer[]) {
  const lexicalEditor = editor?.getEditor()
  if (!lexicalEditor) return

  const serversBySlug = new Map(servers.map((server) => [server.slug.toLowerCase(), server]))

  lexicalEditor.update(() => {
    const root = $getRoot()
    root.clear()

    const paragraph = $createParagraphNode()
    const lines = value.split("\n")
    lines.forEach((line, lineIndex) => {
      const mentionPattern = /@([a-zA-Z0-9_-]+)/g
      let cursor = 0
      let match: RegExpExecArray | null

      while ((match = mentionPattern.exec(line)) !== null) {
        const previousCharacter = line[match.index - 1]
        if (previousCharacter && /[a-zA-Z0-9_-]/.test(previousCharacter)) continue

        const server = serversBySlug.get(match[1].toLowerCase())
        if (!server) continue

        if (match.index > cursor) paragraph.append($createTextNode(line.slice(cursor, match.index)))
        paragraph.append($createExternalMCPMentionNode(server))
        cursor = match.index + match[0].length
      }

      if (cursor < line.length) paragraph.append($createTextNode(line.slice(cursor)))
      if (lineIndex < lines.length - 1) paragraph.append($createLineBreakNode())
    })
    root.append(paragraph)
  })
}

function PromptValuePlugin({ onChange }: { onChange: (value: string) => void }) {
  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(editorState) => {
        editorState.read(() => onChange($getRoot().getTextContent()))
      }}
    />
  )
}

function AttachmentBackspacePlugin({ onEmptyBackspace }: { onEmptyBackspace: () => boolean }) {
  const [editor] = useLexicalComposerContext()

  useEffect(
    () =>
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        () => ($getRoot().getTextContent() ? false : onEmptyBackspace()),
        COMMAND_PRIORITY_LOW
      ),
    [editor, onEmptyBackspace]
  )

  return null
}

function AttachmentPastePlugin({ onPasteFiles }: { onPasteFiles: (files: File[]) => void }) {
  const [editor] = useLexicalComposerContext()

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (!(event instanceof ClipboardEvent)) return false

          const files = Array.from(event.clipboardData?.items ?? [])
            .filter((item) => item.kind === "file")
            .map((item) => item.getAsFile())
            .filter((file): file is File => file !== null)
          if (files.length === 0) return false

          event.preventDefault()
          onPasteFiles(files)
          return true
        },
        COMMAND_PRIORITY_LOW
      ),
    [editor, onPasteFiles]
  )

  return null
}

function ExternalMCPTypeaheadPlugin({
  servers,
  canReconnect,
  reconnecting,
  onReconnect,
  onOpenChange,
}: {
  servers: ExternalMCPServer[]
  canReconnect: boolean
  reconnecting: boolean
  onReconnect: (server: ExternalMCPServer) => void
  onOpenChange: (open: boolean) => void
}) {
  const [editor] = useLexicalComposerContext()
  const [query, setQuery] = useState("")
  const [menuPortal, setMenuPortal] = useState<HTMLElement | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const triggerFn = useBasicTypeaheadTriggerMatch("@", { minLength: 0, maxLength: 50 })
  const options = useMemo(() => {
    const normalizedQuery = query.toLowerCase()
    return servers
      .filter((server) =>
        [server.slug, server.name, server.description].some((value) => value.toLowerCase().includes(normalizedQuery))
      )
      .slice(0, 6)
      .map((server) => new ExternalMCPOption(server))
  }, [query, servers])

  useEffect(() => {
    const portal = document.createElement("div")
    portal.className = "fixed z-9999"
    document.body.appendChild(portal)
    setMenuPortal(portal)
    return () => portal.remove()
  }, [])

  const positionMenu = useCallback(() => {
    if (!menuPortal || !anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    const width = Math.min(320, window.innerWidth - 16)
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    menuPortal.style.width = `${width}px`
    menuPortal.style.left = `${Math.round(left)}px`
    menuPortal.style.top = `${Math.round(rect.top - 8)}px`
  }, [menuPortal])

  useEffect(() => {
    const reposition = () => positionMenu()
    window.addEventListener("resize", reposition)
    window.addEventListener("scroll", reposition, true)
    return () => {
      window.removeEventListener("resize", reposition)
      window.removeEventListener("scroll", reposition, true)
    }
  }, [positionMenu])

  return menuPortal ? (
    <LexicalTypeaheadMenuPlugin<ExternalMCPOption>
      triggerFn={triggerFn}
      onOpen={() => onOpenChange(true)}
      onClose={() => onOpenChange(false)}
      options={options}
      onQueryChange={(nextQuery) => setQuery(nextQuery ?? "")}
      onSelectOption={(option, nodeToReplace: TextNode | null, closeMenu) => {
        if (option.server.requires_reauthorization) return
        if (!nodeToReplace) return

        onOpenChange(false)

        editor.update(() => {
          const mentionNode = $createExternalMCPMentionNode(option.server)
          nodeToReplace.replace(mentionNode)
          const spaceNode = $createTextNode(" ")
          mentionNode.insertAfter(spaceNode)
          spaceNode.select()
          closeMenu()
        })
      }}
      menuRenderFn={(typeaheadAnchorRef, { selectedIndex, selectOptionAndCleanUp }) => {
        anchorRef.current = typeaheadAnchorRef.current as HTMLElement | null
        requestAnimationFrame(positionMenu)

        const open = !!typeaheadAnchorRef.current

        return open
          ? createPortal(
              <div
                role="listbox"
                aria-label="External MCP servers"
                className="bg-popover text-popover-foreground absolute bottom-0 w-full translate-y-[-8px] overflow-hidden rounded-lg border shadow-md"
              >
                <div className="text-muted-foreground px-3 pt-2.5 pb-1 text-xs font-medium">Connections</div>
                {options.length === 0 ? (
                  <p className="text-muted-foreground px-3 py-3 text-sm">No matching server</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto p-1">
                    {options.map((option, index) => {
                      const server = option.server
                      return (
                        <div
                          key={server.id}
                          role="option"
                          aria-selected={index === (selectedIndex ?? 0)}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2 py-2",
                            index === (selectedIndex ?? 0) && "bg-accent"
                          )}
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-2.5 text-left disabled:cursor-not-allowed"
                            disabled={server.requires_reauthorization}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOptionAndCleanUp(option)}
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-sm leading-5 font-medium">{server.name}</span>
                              <span className="text-muted-foreground block truncate text-xs leading-4">
                                {server.description}
                              </span>
                            </span>
                          </button>
                          {server.requires_reauthorization && (
                            <div className="flex shrink-0 items-center gap-1.5">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-red-accent flex items-center gap-1 text-xs font-medium">
                                    <CircleAlert className="size-4" />
                                    Reconnect required
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {canReconnect
                                    ? `Reconnect ${server.name} before using it in chat.`
                                    : `Ask a company admin to reconnect ${server.name}.`}
                                </TooltipContent>
                              </Tooltip>
                              {canReconnect && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="xs"
                                  disabled={reconnecting}
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => onReconnect(server)}
                                >
                                  {reconnecting ? <Loader2 className="animate-spin" /> : <Plug />}
                                  Reconnect
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>,
              menuPortal
            )
          : null
      }}
    />
  ) : null
}

export const ExternalMCPPromptEditor = forwardRef<ExternalMCPPromptEditorHandle, ExternalMCPPromptEditorProps>(
  function ExternalMCPPromptEditor(
    { servers, placeholder, submitDisabled, canReconnect, reconnecting, onReconnect },
    forwardedRef
  ) {
    const editorRef = useRef<EditorRefHandle>(null)
    const hiddenInputRef = useRef<HTMLInputElement>(null)
    const valueRef = useRef("")
    const [inputValue, setInputValue] = useState("")
    const [typeaheadOpen, setTypeaheadOpen] = useState(false)
    const attachments = usePromptInputAttachments()

    const updateValue = useCallback((value: string) => {
      valueRef.current = value
      setInputValue(value)
    }, [])

    const setValue = useCallback(
      (value: string) => {
        updateValue(value)
        setEditorText(editorRef.current, value, servers)
      },
      [servers, updateValue]
    )

    useImperativeHandle(
      forwardedRef,
      () => ({
        clear: () => setValue(""),
        focus: () => editorRef.current?.focus({ defaultSelection: "rootEnd" }),
        getValue: () =>
          editorRef.current
            ?.getEditor()
            ?.getEditorState()
            .read(() => $getRoot().getTextContent()) ?? valueRef.current,
        setValue,
      }),
      [setValue]
    )

    const removeLastAttachment = useCallback(() => {
      const attachment = attachments.files.at(-1)
      if (!attachment) return false
      attachments.remove(attachment.id)
      return true
    }, [attachments])

    return (
      <div data-slot="input-group-control" className="min-h-16 min-w-48 flex-1 py-0 text-base">
        <input ref={hiddenInputRef} type="hidden" name="message" value={inputValue} />
        <Editor
          ref={editorRef}
          isPlainText
          startingPlaceholder={placeholder}
          proseClasses="max-w-none text-base leading-6 [&_p]:m-0"
          styleOverrides={{
            "--lexical-editor-font-size": "16px",
            "--lexical-editor-min-height": "64px",
            "--lexical-editor-max-height": "192px",
            "--lexical-editor-placeholder-left": "0px",
            "--lexical-editor-placeholder-top": "0px",
          }}
          onEnter={() => {
            if (!submitDisabled) hiddenInputRef.current?.form?.requestSubmit()
          }}
          shouldIgnoreEnter={() => typeaheadOpen}
        >
          <PromptValuePlugin onChange={updateValue} />
          <AttachmentBackspacePlugin onEmptyBackspace={removeLastAttachment} />
          <AttachmentPastePlugin onPasteFiles={attachments.add} />
          <ExternalMCPTypeaheadPlugin
            servers={servers}
            canReconnect={canReconnect}
            reconnecting={reconnecting}
            onReconnect={onReconnect}
            onOpenChange={setTypeaheadOpen}
          />
        </Editor>
      </div>
    )
  }
)
